import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useId } from 'react';
import { AppRoutes, buildRoute } from '@/route/routeConfig';
import { capitalize } from '../../../../../../utils';

const projectPhase = ['acquisition', 'finance', 'procurement', 'development', 'design', 'construction'] as const;
type ProjectPhase = (typeof projectPhase)[number];

const projectTabBaseClassName = 'whitespace-nowrap text-center py-4 border-b-4 font-bold text-md bg-white w-full';
const projectTabActiveClassName = 'border-primaryColor-light text-primaryColor-normal';
const projectTabInactiveClassName = 'border-transparent hover:border-primaryColor-light hover:text-primaryColor-normal';

const normalizePathname = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

export const getProjectRootRoute = (projectId: string | number) => buildRoute(AppRoutes.SHERPA_PROJECT_DETAIL, { projectId });

export const getProjectDashboardRoute = (projectId: string | number) => buildRoute(AppRoutes.SHERPA_PROJECT_DASHBOARD, { projectId });

export const getProjectPhaseRoute = (projectId: string | number, phase: string) =>
  buildRoute(AppRoutes.SHERPA_PROJECT_PHASE, { projectId, phase });

export const isProjectTabRouteActive = ({
  pathname,
  tabRoute,
  projectRootRoute,
}: {
  pathname: string;
  tabRoute: string;
  projectRootRoute?: string;
}) => {
  const normalizedPathname = normalizePathname(pathname);

  return (
    normalizedPathname === normalizePathname(tabRoute) || (projectRootRoute && normalizedPathname === normalizePathname(projectRootRoute))
  );
};

export const getProjectSelectedRoute = (projectId: string | undefined, phase: string | undefined, pathname?: string) => {
  if (!projectId) {
    return buildRoute(AppRoutes.SHERPA_PROJECTS);
  }

  const matchedPhase = projectPhase.find(
    (tab) => normalizePathname(pathname ?? '') === normalizePathname(getProjectPhaseRoute(projectId, tab))
  );
  if (matchedPhase) {
    return getProjectPhaseRoute(projectId, matchedPhase);
  }

  if (phase && projectPhase.includes(phase as ProjectPhase)) {
    return getProjectPhaseRoute(projectId, phase);
  }

  return getProjectDashboardRoute(projectId);
};

interface IProjectNavProps {
  hasPhaseAccess: Partial<Record<ProjectPhase, boolean>>;
}
const ProjectNav = ({ hasPhaseAccess }: IProjectNavProps) => {
  const { projectId, phase } = useParams<{ projectId: string; phase: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const selectedTabId = useId();
  const projectDashboardRoute = projectId ? getProjectDashboardRoute(projectId) : buildRoute(AppRoutes.SHERPA_PROJECTS);
  const projectRootRoute = projectId ? getProjectRootRoute(projectId) : undefined;
  const selectedRoute = getProjectSelectedRoute(projectId, phase, pathname);
  const isDashboardActive = isProjectTabRouteActive({ pathname, tabRoute: projectDashboardRoute, projectRootRoute });

  return (
    <>
      <div className='xl:hidden'>
        <label htmlFor={selectedTabId} className='sr-only'>
          Select a tab
        </label>
        <select
          id={selectedTabId}
          name='selected-tab'
          className='mt-1 block w-full cursor-pointer pl-3 pr-10 py-4 border-neutralColor-light focus:outline-hidden rounded-md capitalize bg-white text-primaryColor-normal font-medium'
          value={selectedRoute}
          onChange={(e) => navigate(e.target.value)}
        >
          <option value={projectDashboardRoute}>Dashboard</option>
          {projectPhase.map((tab) => (
            <option key={tab} value={projectId ? getProjectPhaseRoute(projectId, tab) : buildRoute(AppRoutes.SHERPA_PROJECTS)}>
              {capitalize(tab)}
            </option>
          ))}
        </select>
      </div>
      <div className='hidden xl:block'>
        <nav className='flex justify-between items-center w-full rounded-md shadow-md'>
          <Link
            to={projectDashboardRoute}
            aria-current={isDashboardActive ? 'page' : undefined}
            className={`${projectTabBaseClassName} ${isDashboardActive ? projectTabActiveClassName : projectTabInactiveClassName}`}
          >
            Dashboard
          </Link>

          {projectPhase.map((tab) => {
            if (hasPhaseAccess[tab]) {
              const phaseRoute = projectId ? getProjectPhaseRoute(projectId, tab) : buildRoute(AppRoutes.SHERPA_PROJECTS);
              const isPhaseActive = isProjectTabRouteActive({ pathname, tabRoute: phaseRoute });
              return (
                <Link
                  key={tab}
                  to={phaseRoute}
                  aria-current={isPhaseActive ? 'page' : undefined}
                  className={`${projectTabBaseClassName} capitalize ${isPhaseActive ? projectTabActiveClassName : projectTabInactiveClassName}`}
                >
                  {tab}
                </Link>
              );
            }
            return null;
          })}
        </nav>
      </div>
    </>
  );
};

export default ProjectNav;
